import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import ComposeBox from './Feed/ComposeBox';
import PostCard from './Feed/PostCard'; // Adjust path if needed

export default function ProfileView({ 
  profile, 
  setActiveModal, 
  showToast, 
  currentUser, 
  setCurrentView, 
  setTargetSettingsTab,
  targetPostId,
  targetCommentId, // 🆕 ADDED targetCommentId PROP
  onToggleFollow // 🆕 New function to handle the 1-way follow/unfollow
}) {
  
  const [connectStatus, setConnectStatus] = useState(null);

  // --- STATES & HOOKS ---
  const [activeSubTab, setActiveSubTab] = useState(targetPostId ? 'posts' : 'portfolio');
  const [isFav, setIsFav] = useState(false);
  const [insights, setInsights] = useState({ 
    profile_views: 0, 
    portfolio_clicks: 0,
    booking_requests: 0,
    scout_views: 0
  });

  // Post management
  const [profilePosts, setProfilePosts] = useState([]);
  const [expandedComments, setExpandedComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [replyTarget, setReplyTarget] = useState({});


  // Check user comments default setting
  const userCommentDefault = !(
    currentUser?.default_comments_state === 'Disabled' || 
    currentUser?.default_comments_state === false || 
    currentUser?.default_comments_state === 0 || 
    currentUser?.default_comments_state === '0'
  );

  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // 1. Keep track of the follow state locally so the UI updates instantly
  // Initialize them as false, the useEffect below will correct them immediately
  const [localFollowing, setLocalFollowing] = useState(false);

  // Sync profile data whenever the parent passes a new profile in
  useEffect(() => {
    if (profile) {
      // Sync Posts
      if (profile.posts) {
        setProfilePosts(profile.posts);
      }
      
      // 🚨 Sync the new backend booleans to the UI states
      setIsFav(profile.is_favourited || false);
      setLocalFollowing(profile.is_following || false);
    }
  }, [profile]);


  // 🎯 Handle targetPostId AND targetCommentId (Auto-expand, fetch, scroll & highlight)
  useEffect(() => {
    if (targetPostId) {
      // 1. Force UI to switch to the 'posts' subtab
      setActiveSubTab('posts');

      // 2. Polling function to wait for the DOM element to actually render
      const scrollToElement = (elementId, highlightColor) => {
        let attempts = 0;
        const interval = setInterval(() => {
          const el = document.getElementById(elementId);
          if (el) {
            clearInterval(interval); // Stop looking once found
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Apply highlight
            el.style.transition = 'background-color 0.5s';
            el.style.backgroundColor = highlightColor;
            setTimeout(() => { el.style.backgroundColor = 'transparent'; }, 2000);
          }
          attempts++;
          // Give up after 3 seconds (30 attempts * 100ms)
          if (attempts > 30) clearInterval(interval);
        }, 100);
      };

      if (targetCommentId) {
        // 3. Expand the comment tray immediately
        setExpandedComments(prev => ({ ...prev, [targetPostId]: true }));

        // 4. Fetch comments if they aren't loaded, then scroll
        if (!postComments[targetPostId]) {
          api.get(`/posts/${targetPostId}/comments`)
            .then(res => {
              const fetchedComments = res.data.comments || res.data;
              setPostComments(prev => ({ ...prev, [targetPostId]: fetchedComments }));
              scrollToElement(`comment-${targetCommentId}`, 'rgba(0, 255, 170, 0.2)');
            })
            .catch(err => console.error("Error auto-fetching comments:", err));
        } else {
          // If comments are already in state, just scroll
          scrollToElement(`comment-${targetCommentId}`, 'rgba(0, 255, 170, 0.2)');
        }
      } else {
        // Fallback: Scroll to post if there's no target comment
        scrollToElement(`post-${targetPostId}`, 'var(--purple-l)');
      }
    }
  // Removed `postComments` from dependencies so this doesn't accidentally re-run when someone types a new comment

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPostId, targetCommentId]);


  // Fetch insights
  useEffect(() => {
    if (activeSubTab === 'insights') {
      const getInsights = async () => {
        try {
          const res = await api.get('/profiles/me/insights');
          setInsights(res.data.insights);
        } catch (err) {
          console.error('Failed to load insights:', err);
        }
      };
      getInsights();
    }
  }, [activeSubTab]);



  // 1. --- USER & OWNER EVALUATION --- (Safe to run first because of ?.)
  const currentUserId = currentUser?.user_id || currentUser?.id;
  
  // Explicitly grab the UUID (Ignore handle)
  const targetId = profile?.user_id || profile?.id;

  const isOwner = Boolean(
    currentUserId && 
    targetId && 
    String(currentUserId) === String(targetId)
  );

  // 2. --- HOOKS --- (Must be here, before the early return)
  useEffect(() => {
    const fetchConnectionStatus = async () => {
      const targetUuid = profile?.user_id || profile?.id; 
      
      // If we don't have an ID yet, stop here.
      if (!targetUuid) return;

      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(targetUuid);

      if (isUUID && !isOwner) {
        try {
          const res = await api.get(`/connections/status/${targetUuid}`);
          setConnectStatus(res.data.status); 
        } catch (err) {
          console.error('Error fetching connection status:', err);
        }
      } else if (!isUUID && !isOwner) {
        // This will print in your browser console so you know if it's trying to use a handle!
        console.warn("ProfileView: Skipped connection check. Expected UUID, but got:", targetUuid);
      }
    };

    fetchConnectionStatus();
  }, [profile, isOwner]);


  // 3. --- EARLY EXIT --- (Only AFTER all hooks are declared)
  if (!profile) return <div className="view active">Loading profile...</div>;

   const handleAvatarClick = () => {
    if (isOwner) {
      setTargetSettingsTab('account');
      setCurrentView('settings'); 
    } else {
      setActiveModal('story-upload-modal');
    }
  };

  const handleToggleFav = async (targetId) => {
    const previousFavState = isFav;
    setIsFav(!isFav);
  
    try {
      const response = await api.post(`/favourites/${targetId}`);
      if (response.data) {
        showToast(previousFavState ? "Removed from favorites" : "Added to favorites!");
      }
    } catch (error) {
      setIsFav(previousFavState);
      showToast('Failed to update favorites. Please try again.');
      console.error("Error toggling favorite:", error);
    }
  };

  const formatFollowers = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num;
  };
  
  const totalFollowers = profile?.platform_links?.reduce((sum, link) => {
    return sum + (Number(link.follower_count) || 0);
  }, 0) || 0;

  const handlePortfolioClick = (url) => {
    if (!profile?.user_id) return;
    api.post('/profiles/track-click', { targetUserId: profile.user_id })
       .catch(err => console.error("Tracking failed:", err));
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 2. The function to handle the database request
  const handleFollowToggle = async () => {
    // Close the dropdown immediately
    setShowMoreMenu(false);

    // Optimistic UI Update (swaps instantly before the DB responds)
    const previousState = localFollowing;
    setLocalFollowing(!previousState);

    try {
      // NOTE: Adjust '/follows/' if your route in app.js is mounted as '/users/'
      const response = await api.post(`/follows/${targetId}/follow`);
      
      // Sync exactly with what the backend returns
      setLocalFollowing(response.data.is_following);
      showToast(response.data.message);
      
      // If Dashboard passed a function, trigger it to keep parent state in sync
      if (onToggleFollow) onToggleFollow(response.data.is_following);
      
    } catch (error) {
      // If the API fails, revert the button back to its previous state
      setLocalFollowing(previousState);
      showToast(error.response?.data?.message || "Failed to update follow status.");
      console.error("Follow error:", error);
    }
  };

  const formatEventDate = (dateString) => {
    if (!dateString) return { day: '??', mon: '???' };
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const mon = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    return { day, mon };
  };

  const handlePostSubmit = async (payload) => {
    try {
      const res = await api.post('/posts', {
        body: payload.text,
        audience: payload.audience === 'Everyone' ? 'public' : 'connections',
        image_url: payload.hasImage ? 'https://via.placeholder.com/600x400?text=Sample+Image' : null,
        link_url: payload.linkUrl || null,
        allow_comments: payload.allowComments
      });
      const newPost = {
        ...res.data.post,
        allow_comments: payload.allowComments 
      };
      setProfilePosts([newPost, ...profilePosts]);
      showToast('Post published to your profile!');
    } catch (err) {
      showToast('Failed to publish post.');
    }
  };

  const handleVote = async (postId, voteValue) => {
    try {
      // 1. Send the correct numerical payload (1 or -1)
      const response = await api.post(`/posts/${postId}/vote`, { vote_value: voteValue });
      
      // 2. If successful, update the UI with the exact score the database returned
      if (response.data && response.data.status === 'success') {
        const actualDatabaseScore = response.data.new_score;
        
        // Use your specific state setter for this view: setProfilePosts
        setProfilePosts(current => current.map(post => 
          post.id === postId 
            ? { ...post, vote_score: actualDatabaseScore } 
            : post
        ));
      }
    } catch (error) {
      console.error("Vote failed:", error);
      showToast('Failed to register vote'); 
    }
  };

  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    setExpandedComments({ ...expandedComments, [postId]: !isExpanded });

    if (!isExpanded && !postComments[postId]) {
      try {
        const res = await api.get(`/posts/${postId}/comments`);
        setPostComments({ ...postComments, [postId]: res.data.comments || res.data });
      } catch (err) {
        showToast("Error loading comments");
      }
    }
  };

  const submitComment = async (postId, text) => {
    if (!text || text.trim() === '') return;
    const targetComment = replyTarget[postId];
  
    try {
      let res;
      if (targetComment) {
        res = await api.post(`/posts/comments/${targetComment.id}/reply`, { body: text });
      } else {
        res = await api.post(`/posts/${postId}/comments`, { body: text });
      }
  
      const newComment = res.data?.comment || res.data?.reply || res.data;
      setPostComments((prev) => ({ ...prev, [postId]: [newComment, ...(prev[postId] || [])] }));
      setReplyTarget((prev) => ({ ...prev, [postId]: null }));
      setProfilePosts((currentPosts) => currentPosts.map(p => 
        p.id === postId ? { ...p, comment_count: parseInt(p.comment_count || 0) + 1 } : p
      ));
      showToast(targetComment ? "Reply sent!" : "Comment added!");
    } catch (err) {
      console.error("Error posting comment:", err);
      showToast("Error posting message");
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${postId}`);
      showToast('Post deleted successfully');
      if (typeof setProfilePosts === 'function') {
        setProfilePosts(prevPosts => prevPosts.filter(post => post.id !== postId));
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete post');
    }
  };

  const handleEditPostSubmit = async (postId, newText) => {
    if (!newText || newText.trim() === '') {
      showToast('Post cannot be empty.');
      return;
    }
    
    try {
      // 1. Send the new text to the database
      await api.patch(`/posts/${postId}`, { body: newText });
      
      // 2. Update the frontend UI state with the new text
      setProfilePosts(prevPosts => prevPosts.map(post => 
        post.id === postId ? { ...post, body: newText } : post
      ));
      
      showToast('Post updated successfully!');
    } catch (err) {
      showToast('Failed to update post.');
    }
  };

  // Inside Dashboard.jsx or Profile Component
const handleConnectRequest = async (targetUserId, note = "") => {
  // 1. Optimistic UI update
  setConnectStatus('pending'); 

  try {
      // 2. Matches your existing router.post('/request', sendRequest)
      const response = await api.post('/connections/request', {
          recipient_id: targetUserId,
          note: note // Optional note string
      });
      
      showToast(response.data.message || "Connection request sent!");
  } catch (error) {
      // Revert UI on failure
      setConnectStatus('connect');
      showToast(error.response?.data?.message || 'Failed to send request.');
      console.error("Connection error:", error);
  }
};

  return (
    <div className="view active" id="view-profile">
      <div className="banner-notice" id="upgrade-notice">
        <div style={{ fontSize: '20px' }}>🔒</div>
        <div className="bn-text">
          <div className="bn-title">Starter plan — 4 platform limit</div>
          <div className="bn-sub">Upgrade to Mid or Premium for unlimited platforms and AI tools.</div>
        </div>
        <button className="bn-btn" onClick={() => setActiveModal('upgrade-modal')}>Upgrade now</button>
      </div>
      
      <div className="profile-banner"><div className="banner-pattern"></div></div>

      <div className="profile-card">
        <div className="avatar-row">
          <div 
            className="profile-big-av" 
            onClick={handleAvatarClick} 
            style={{ cursor: 'pointer', position: 'relative' }}
            title={isOwner ? "Edit Avatar in Settings" : "View Story"}
          >
            <div className="story-outer">
              <div className="story-outer-inner" style={{ overflow: 'hidden', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--card)' }}>
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: '36px' }}>🎨</span>
                )}
              </div>
            </div>
            {isOwner && (
              <div style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--purple)', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', border: '2px solid var(--bg)' }}>
                ✏️
              </div>
            )}
          </div>

          <div className="action-bar">
  {!isOwner && (
    <>
      {/* 2-WAY CONNECT BUTTON */}
      <button 
        className={`btn btn-connect ${connectStatus === 'pending' ? 'pending' : connectStatus === 'accepted' ? 'connected' : ''}`}
        onClick={() => handleConnectRequest(profile.user_id)} 
        disabled={connectStatus === 'pending'}
      >
        {connectStatus === 'pending' ? 'Pending' : connectStatus === 'accepted' ? 'Connected' : 'Connect'}
      </button>

      <button 
        className={`btn btn-follow ${localFollowing ? 'Following' : 'Follow'}`}
        onClick={handleFollowToggle}
      >
        {localFollowing ? 'Following' : 'Follow'}
      </button>

      <button 
        className={`btn btn-fav ${isFav ? 'saved' : ''}`} 
        onClick={() => handleToggleFav(targetId)}
      >
        <span>{isFav ? '★' : '☆'}</span> <span>{isFav ? 'Favourited' : 'Favourite'}</span>
      </button>

    </>        
  )}
            
  {/* Added 'desktop-share-btn' class here */}
  <button className="btn btn-primary desktop-share-btn" onClick={() => setActiveModal('share-modal')}>↗ Share</button>

  <div className="more-options-container" style={{ position: 'relative' }}>
    <button onClick={() => setShowMoreMenu(!showMoreMenu)} className="post-menu">...</button>

    {showMoreMenu && (
      <div className="dropdown-menu">    
        {!isOwner && (
          <button className="dm-item" onClick={handleFollowToggle}>
            {localFollowing ? 'Unfollow User' : 'Follow User'}
          </button>
        )}

        {/* Hooked up the modal trigger and menu close here */}
        <button 
          className="dm-item" 
          onClick={() => { 
            setActiveModal('share-modal'); 
            setShowMoreMenu(false); 
          }}
        >
          Share Profile
        </button>
        
        <button className="dm-item">Mute User</button>

        <div className="dm-divider"></div>

        <button className="dm-item danger">Block User</button>
      </div>
    )}
  </div> 
</div>        
</div>
        
        <div className="profile-name">{profile.display_name} <span className="verified-icon">✦</span></div>
        <div className="profile-handle">@{profile.handle} · buniverse.io/{profile.handle}</div>
        <div className="profile-bio">{profile.bio}</div>
        
        <div className="tags" style={{ marginBottom: 0 }}>
          <div className="tags-row" style={{ marginTop: '12px' }}>
            {profile?.creative_types && profile.creative_types.length > 0 ? (
              profile.creative_types.map((tag, index) => {
                const colors = ['tg-p', 'tg-t', 'tg-a'];
                const colorClass = colors[index % colors.length];
                return (
                  <span key={index} className={`tag ${colorClass}`}>
                    {tag}
                  </span>
                );
              })
            ) : (
              <span className="tag tg-a">Creative Explorer</span>
            )}
          </div>
        </div>
        
        <div className="stats-row">
          <div className="stat-box"><div className="stat-n">4.2k</div><div className="stat-l">Followers</div></div>
          <div className="stat-box"><div className="stat-n">48</div><div className="stat-l">Works</div></div>
          <div className="stat-box"><div className="stat-n">12</div><div className="stat-l">Collabs</div></div>
          <div className="stat-box"><div className="stat-n">214</div><div className="stat-l">Reviews</div></div>
        </div>
      </div>

      <div className="rating-bar">
        <div><div className="big-score">4.8</div><div className="stars">★★★★★</div><div className="rating-sub">214 reviews</div></div>
        <div className="rb">
          <div className="rb-row"><div className="rb-lbl">Creativity</div><div className="rb-track"><div className="rb-fill" style={{ width: '95%' }}></div></div></div>
          <div className="rb-row"><div className="rb-lbl">Delivery</div><div className="rb-track"><div className="rb-fill" style={{ width: '90%' }}></div></div></div>
          <div className="rb-row"><div className="rb-lbl">Comms</div><div className="rb-track"><div className="rb-fill" style={{ width: '88%' }}></div></div></div>
          <div className="rb-row"><div className="rb-lbl">Value</div><div className="rb-track"><div className="rb-fill" style={{ width: '92%' }}></div></div></div>
        </div>
      </div>

      <div className="sub-tabs">
        {['portfolio', 'socials', 'posts', 'upcoming', 'reviews', 'press', 'insights'].map((tab) => (
          <button 
            key={tab} 
            className={`sub-tab ${activeSubTab === tab ? 'active' : ''}`} 
            onClick={() => setActiveSubTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeSubTab === 'portfolio' && (
        <div className="sub-screen active">
          <div className="section-lbl">
            {formatFollowers(totalFollowers)} total audience across {profile?.platform_links?.length || 0} platforms
          </div>

          <div className="port-grid">
            {profile?.portfolio_highlights && profile.portfolio_highlights.length > 0 ? (
              profile.portfolio_highlights.map((item, i) => (
                <div 
                  key={i} 
                  className="port-item" 
                  style={{ 
                    cursor: 'pointer', 
                    backgroundImage: item.thumbnail_url ? `url(${item.thumbnail_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'var(--purple-l)'
                  }}
                  onClick={() => handlePortfolioClick(item.link_url)}
                >
                  <div style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', width: '100%', height: '100%', display: 'flex', alignItems: 'flex-end', padding: '10px' }}>
                     <span className="port-src" style={{ color: '#fff', fontWeight: 'bold' }}>{item.platform}</span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '1rem', color: 'var(--text3)' }}>No portfolio items uploaded yet.</div>
            )}
          </div>

          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem', marginTop: '4px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontFamily: "'Syne', sans-serif", marginBottom: '4px' }}>Available for collaboration</div>
            <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: '10px' }}>Book a paid session to discuss your project.</div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Book a session</button>
          </div>
        </div>
      )}

      {activeSubTab === 'socials' && (
        <div className="sub-screen active">
          <div className="section-lbl">Linked platforms</div>
          {profile?.platform_links && profile.platform_links.length > 0 ? (
            profile.platform_links.map((link) => (
              <div key={link.id} className="soc-row" style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="si" style={{ background: 'var(--blue-l)' }}>🔗</div>
                <div>
                  <div className="soc-name">{link.platform}</div>
                  <div className="soc-meta">{link.follower_count || 0} followers · @{link.username}</div>
                </div>
                <button className="visit-btn" onClick={() => window.open(link.profile_url, '_blank')}>Visit</button>
              </div>
            ))
          ) : (
            <div style={{ padding: '1rem', color: 'var(--text3)' }}>No platforms linked yet.</div>
          )}
        </div>
      )}

      {activeSubTab === 'posts' && (
        <div className="sub-screen active">
          <ComposeBox 
            onPostSubmit={handlePostSubmit} 
            showToast={showToast} 
            defaultComments={userCommentDefault} 
          />
          
          <div className="section-lbl" style={{ marginTop: '20px' }}>
            {profile?.display_name || 'User'}'s posts & topics
          </div>

          {profilePosts && profilePosts.length > 0 ? (
            profilePosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={currentUser}
                onVote={handleVote}
                onSharePost={() => setActiveModal('share-modal')}
                onDeletePost={deletePost}
                showToast={showToast}
          
                // Comment handling
                isCommentsExpanded={!!expandedComments[post.id]}
                onToggleComments={toggleComments}
                postComments={postComments[post.id] || []}
                onAddComment={submitComment}
          
                // Reply & Edit routing
                replyTarget={replyTarget[post.id]}
                onSetReplyTarget={(comment) => setReplyTarget({ ...replyTarget, [post.id]: comment })}
                onEditPost={handleEditPostSubmit}
          
                // 🎯 Crucial: Passing the Notification Targets here!
                targetPostId={targetPostId}
                targetCommentId={targetCommentId}
              />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text3)', fontSize: '14px' }}>
              No posts to show yet.
            </div>
          )}

        </div>
      )}

      {activeSubTab === 'upcoming' && (
        <div className="sub-screen active" id="sub-upcoming">
          <div className="section-lbl">Upcoming Events</div>

          {profile?.upcoming_events && profile.upcoming_events.length > 0 ? (
            profile.upcoming_events.map((event) => {
              const { day, mon } = formatEventDate(event.event_date);
              return (
                <div 
                  className="uc-item" 
                  key={event.id}
                  style={{ cursor: event.ticket_url ? 'pointer' : 'default' }}
                  onClick={() => event.ticket_url && window.open(event.ticket_url, '_blank')}
                >
                  <div className="uc-date">
                    <div className="uc-day">{day}</div>
                    <div className="uc-mon">{mon}</div>
                  </div>
                  <div>
                    <div className="uc-title">{event.event_name}</div>
                    <div className="uc-detail">
                      {event.location} · {event.is_free ? 'Free entry' : 'Ticketed'}
                    </div>
                  </div>
                  {event.ticket_url && <div style={{ marginLeft: 'auto', color: 'var(--text3)' }}>↗</div>}
                </div>
              );
            })
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--text3)', marginBottom: '10px' }}>No upcoming events scheduled.</div>
          )}

          <div className="upload-area" onClick={() => showToast('Go to Settings > Content to schedule an event!')}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>📅</div>
            <div style={{ fontSize: '11px', color: 'var(--text3)' }}>Schedule an upcoming event</div>
          </div>
        </div>
      )}

      {activeSubTab === 'reviews' && (
        <div className="sub-screen active">
          <div className="rev-item"><div className="rev-top"><div className="rev-av" style={{ background: 'var(--purple-l)', color: 'var(--purple)' }}>KM</div><div className="rev-name">Kofi Mensah</div><div className="rev-stars">★★★★★</div></div><div className="rev-text">Exceptional work.</div></div>
        </div>
      )}

      {activeSubTab === 'press' && (
        <div className="sub-screen active" id="sub-press">
          <div className="section-lbl">Press Articles</div>
          {profile?.press_links && profile.press_links.length > 0 ? (
            profile.press_links.map((press) => (
              <div className="press-item" key={press.id}>
                <div className="press-icon" style={{ background: 'var(--blue-l)' }}>📰</div>
                <div>
                  <div className="press-title">{press.title}</div>
                  <div className="press-pub">{press.publisher} · {press.publish_date}</div>
                  <a href={press.link_url} target="_blank" rel="noopener noreferrer" className="press-link" style={{ textDecoration: 'none' }}>↗ Read article</a>
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--text3)', marginBottom: '10px' }}>No press links added yet.</div>
          )}

          <div className="upload-area" onClick={() => showToast('Go to Settings > Content to add articles!')}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>📰</div>
            <div style={{ fontSize: '11px', color: 'var(--text3)' }}>Upload a press article or paste link</div>
          </div>

          <div className="section-lbl" style={{ marginTop: '16px' }}>Awards</div>
          {profile?.awards && profile.awards.length > 0 ? (
            profile.awards.map((award) => (
              <div className="award-item" key={award.id}>
                <div style={{ fontSize: '24px' }}>🏆</div>
                <div>
                  <div className="award-title">{award.award_title} — {award.organization}</div>
                  <div className="award-year">{award.award_year} · {award.award_tier}</div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: '14px', color: 'var(--text3)', marginBottom: '10px' }}>No awards added yet.</div>
          )}

          <div className="upload-area" onClick={() => showToast('Go to Settings > Content to add awards!')}>
            <div style={{ fontSize: '22px', marginBottom: '6px' }}>🏆</div>
            <div style={{ fontSize: '11px', color: 'var(--text3)' }}>Add an award or recognition</div>
          </div>
        </div>
      )}

      {activeSubTab === 'insights' && (
        <div className="sub-screen active" id="sub-insights">
          <div className="ins-grid">
            <div className="ins-card">
              <div className="ins-lbl">Profile views</div>
              <div className="ins-val">{insights?.profile_views?.toLocaleString()}</div>
              <div className="ins-chg">+24%</div>
            </div>
            <div className="ins-card">
              <div className="ins-lbl">Portfolio clicks</div>
              <div className="ins-val">{insights?.portfolio_clicks?.toLocaleString()}</div>
              <div className="ins-chg">+11%</div>
            </div>
            <div className="ins-card">
              <div className="ins-lbl">Booking requests</div>
              <div className="ins-val">{insights?.booking_requests?.toLocaleString()}</div>
              <div className="ins-chg">All time</div>
            </div>
            <div className="ins-card">
              <div className="ins-lbl">Scout views</div>
              <div className="ins-val">{insights?.scout_views?.toLocaleString()}</div>
              <div className="ins-chg" style={{ color: 'var(--coral)' }}>Tracking active</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}